import React, { memo, useCallback, useMemo, useState } from 'react'
import { Modal, Typography } from '@mui/material'
import tw from 'twin.macro'
import { showToast } from 'src/utils/toast'
import { Button } from 'src/components/shared/Button'
import { GSIcon } from 'src/components/shared/GSIcon'
import { useSelector } from 'react-redux'
import { getOnboardingTemplates } from 'src/selectors/onboarding'
import { getOnBoardingStepsNames } from 'src/components/onboarding/invite-workers/InviteWorkersUtils'
import { BasicSelect } from 'src/components/shared/form/BasicSelect'
import { colors } from 'src/theme'

interface Props {
  isOpen?: boolean
  currentTemplateId: string
  onSubmit(templateId?: string): void | Promise<void>
  onClose(): void
}

const MCLiteUnassignToModalComponent = ({
  isOpen,
  currentTemplateId,
  onSubmit,
  onClose,
}: Props) => {
  const onboardingTemplates = useSelector(getOnboardingTemplates)

  const [isLoading, setIsLoading] = useState(false)
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | undefined>(undefined)

  const container = useMemo(() => document.getElementById('root'), [])
  const currentTemplate = useMemo(() => {
    return onboardingTemplates.find((template) => template.id === currentTemplateId)
  }, [currentTemplateId, onboardingTemplates])

  const handleSubmit = useCallback(async () => {
    setIsLoading(true)

    try {
      await onSubmit(selectedTemplateId)
    } catch (err) {
      showToast(err as any, { variant: 'error' })
    }

    setIsLoading(false)
    setSelectedTemplateId(undefined)
  }, [onSubmit, selectedTemplateId])

  const handleChangeTemplate = useCallback((event: React.ChangeEvent<any>) => {
    const selectedTemplateId = event.target.value as string

    setSelectedTemplateId(selectedTemplateId)
  }, [])

  return (
    <Modal
      container={container}
      open={Boolean(isOpen)}
      onClose={onClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
      css={tw`flex justify-center items-center backdrop-blur p-4`}
    >
      <div
        data-testid="contractor-details-mc-lite-unassign-modal"
        css={tw`w-[474px] flex-col bg-white rounded-lg`}
      >
        <div css={tw`p-4 items-center flex flex-col`}>
          <GSIcon iconName="gs_unlocked" />
          <Typography css={tw`mt-2 mb-1`} variant="h4">
            Unassign contractor
          </Typography>
          <Typography css={tw`text-center w-3/4`} color={colors.SHADES_GREY_400} variant="body2">
            Contractor status will be changed from Subcontractor (Sub) to Individual Contractor
            (IC).
          </Typography>
          <div css={tw`w-full border-0 border-t border-solid my-4 border-[#EDEDED]`} />
          <Typography variant="h5">Onboarding template</Typography>
          <Typography
            css={tw`text-center mt-1 mb-4 w-4/5`}
            color={colors.SHADES_GREY_400}
            variant="body2"
          >
            If you'd like to update the onboarding template, select a new one below — or leave empty
            to keep the current template.
          </Typography>
          <div
            css={tw`w-full flex flex-col mb-4 border border-solid border-[#EDEDED] rounded-2xl p-4`}
          >
            <Typography variant="h6">Current Template</Typography>
            <Typography color={colors.SHADES_GREY_500} variant="body2">
              {currentTemplate?.name || ''}
            </Typography>
            <div css={tw`w-full border-0 border-t border-solid my-4 border-[#EDEDED]`} />
            <Typography color={colors.SHADES_GREY_500} variant="body2">
              {getOnBoardingStepsNames(onboardingTemplates, currentTemplateId) || ''}
            </Typography>
          </div>
          <div
            css={tw`w-full flex flex-col mb-2 border border-solid border-[#EDEDED] rounded-2xl p-4`}
          >
            <Typography variant="h6" css={tw`mb-1.5`}>
              New onboarding template
            </Typography>
            <div css={tw`w-[216px]`}>
              <BasicSelect
                placeholder="Select template"
                value={selectedTemplateId}
                options={onboardingTemplates.map((item) => ({
                  label: item.name,
                  value: item.id,
                }))}
                onChange={handleChangeTemplate}
              />
            </div>
          </div>
        </div>
        <div
          css={tw`flex justify-between items-center px-4 py-3 border-0 border-t border-solid  border-[#EDEDED]`}
        >
          <Button
            className={tw`px-8`}
            disabled={isLoading}
            onClick={onClose}
            color="grey"
            size="small"
            data-testid="contractor-details-mc-lite-unassign-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            className={tw`px-8`}
            loading={isLoading}
            onClick={handleSubmit}
            color="light"
            size="small"
            data-testid="contractor-details-mc-lite-unassign-continue-btn"
          >
            Continue
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export const MCLiteUnassignToModal = memo(MCLiteUnassignToModalComponent)
